/**
 * #14 燕窩存摺 — LIFF /wallet
 *
 * 視覺化:
 *   - 大字顯示剩餘組數 + 瓶數 (1 組 = 6 瓶)
 *   - 累計入帳 / 累計出帳 (給 user 看歷史總量,類比銀行存摺)
 *   - 出入帳明細 list — 每筆 +/- 顏色區分,日期 + 原因 label + 餘額 snapshot
 *
 * 資料來源 backend 由 wallet_service:
 *   - GET /api/wallet              — balance + derive (sets/remainder/bottles_per_set)
 *   - GET /api/wallet/transactions — 歷史交易(reverse chronological)
 *
 * 當下狀態:
 *   - 真金流尚未接通,credit 來源主要靠 admin 後台手動入帳
 *   - 訂單 status=paid 自動 credit 已寫好 hook,但 mock 訂單不會觸發
 *   - 若 wallet doc 不存在(從未交易過)→ 顯示「尚未開帳」+ 鼓勵下單入帳訊息
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getMyWallet,
  getMyWalletTransactions,
  WALLET_CATEGORY_NAMES,
  WALLET_REASON_LABELS,
  type WalletBalance,
  type WalletTxn,
} from "../api/wallet";
import { useUIStore } from "../store/uiStore";


export default function Wallet() {
  const navigate = useNavigate();
  const [wallet, setWallet] = useState<WalletBalance | null>(null);
  const [txns, setTxns] = useState<WalletTxn[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [w, t] = await Promise.all([
          getMyWallet(),
          getMyWalletTransactions(50),
        ]);
        setWallet(w);
        setTxns(t);
      } catch {
        useUIStore.getState().showToast("載入存摺失敗", "error");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return <div className="min-h-screen bg-cream flex items-center justify-center text-muted">載入中…</div>;
  }
  if (!wallet) {
    return (
      <div className="min-h-screen bg-cream flex flex-col items-center justify-center text-muted gap-4">
        <div>無法載入存摺</div>
        <button onClick={() => navigate("/home")} className="btn-primary">回首頁</button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream pb-12">
      <header className="bg-primary text-white px-5 py-6 flex items-center gap-3">
        <button onClick={() => navigate("/home")} className="text-white text-xl" aria-label="返回">‹</button>
        <h1 className="text-xl font-bold">燕窩存摺</h1>
      </header>

      {/* 品類餘額卡 — 即飲 / 鮮燉 / 留白禮盒各記各的,單位不互換。
          internal 品類(禮盒/紙袋 送禮包材,不對客販售)只在有往來時顯示 —
          管理者送過的人看得到自己有幾個,沒拿過的一般會員完全看不到這兩項 */}
      <div className="px-5 mt-5">
        <div className="rounded-2xl bg-gradient-to-br from-primary to-primary-dark text-white p-6 shadow-md">
          <div className="text-xs opacity-80">霽雲・恆時珍藏</div>
          <div className="mt-4 space-y-4">
            {wallet.categories.filter(
              (c) => !c.internal || c.balance > 0 || c.credited > 0 || c.debited > 0
            ).map((c) => (
              <div key={c.category} className="border-b border-white/15 pb-4 last:border-b-0 last:pb-0">
                <div className="flex items-end justify-between">
                  <div className="text-sm opacity-90">
                    {c.name}
                    {c.spec && <span className="text-xs opacity-70 ml-1">{c.spec}</span>}
                  </div>
                  <div className="flex items-end gap-1">
                    <span className="text-3xl font-bold leading-none">{c.balance.toLocaleString()}</span>
                    <span className="text-sm opacity-90 mb-0.5">{c.unit}</span>
                  </div>
                </div>
                <div className="text-xxs opacity-60 mt-1 text-right">
                  累計入 {c.credited.toLocaleString()} · 出 {c.debited.toLocaleString()} {c.unit}
                </div>
              </div>
            ))}
          </div>

          {/* 既有餘額(舊制單一瓶數)— 訂單入帳的歷史餘額,>0 才顯示,待後續歸類 */}
          {wallet.balance_bottles > 0 && (
            <div className="flex items-center justify-between mt-4 pt-3 border-t border-white/20 text-xs opacity-80">
              <span>既有餘額</span>
              <span>{wallet.balance_bottles.toLocaleString()} 瓶</span>
            </div>
          )}
        </div>
      </div>

      {/* 尚未開帳引導 */}
      {!wallet.exists && (
        <div className="px-5 mt-5">
          <div className="card bg-sage-light/30 text-center py-6 space-y-3">
            <div className="text-sm text-charcoal">
              您的燕窩存摺尚未開帳。
            </div>
            <div className="text-xs text-muted leading-relaxed">
              每筆訂單付款完成後,系統將自動將對應瓶數計入您的存摺,
              <br />
              方便您追蹤庫存與享用節奏。
            </div>
            <button
              onClick={() => navigate("/products")}
              className="btn-primary mt-2"
            >
              開始選購
            </button>
          </div>
        </div>
      )}

      {/* 出入帳明細 */}
      <div className="px-5 mt-5">
        <div className="text-sm text-muted font-medium mb-3 flex items-center justify-between">
          <span>出入帳明細</span>
          <span className="text-xs text-muted">最近 {txns.length} 筆</span>
        </div>
        {txns.length === 0 ? (
          <div className="card text-center text-muted text-sm py-6">尚無明細</div>
        ) : (
          <div className="card space-y-0 p-0">
            {txns.map((tx) => (
              <TxnRow key={tx.txn_id} tx={tx} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


function TxnRow({ tx }: { tx: WalletTxn }) {
  const isCredit = tx.type === "credit";
  const sign = isCredit ? "+" : "−";
  const color = isCredit ? "text-primary" : "text-coral";
  const reason = WALLET_REASON_LABELS[tx.reason] || tx.reason;
  const ts = tx.created_at ? String(tx.created_at).slice(0, 16).replace("T", " ") : "—";
  // 新制品類交易:qty + unit + 品類名;舊制訂單交易:bottles 瓶
  const amount = tx.qty ?? tx.bottles ?? 0;
  const unit = tx.unit || "瓶";
  const catName = tx.category ? WALLET_CATEGORY_NAMES[tx.category] || "" : "";

  return (
    <div className="px-4 py-3 border-b border-line last:border-b-0 flex items-start justify-between gap-3">
      <div className="flex-1 min-w-0">
        <div className="text-sm text-ink font-medium">
          {reason}
          {catName && <span className="ml-1 text-xs text-muted">· {catName}</span>}
        </div>
        {tx.note && (
          <div className="text-xs text-muted mt-0.5 break-words">{tx.note}</div>
        )}
        <div className="text-xxs text-muted mt-1">{ts}</div>
      </div>
      <div className="text-right shrink-0">
        <div className={`text-base font-bold ${color}`}>
          {sign} {amount} {unit}
        </div>
        <div className="text-xxs text-muted mt-0.5">
          餘 {tx.balance_after} {unit}
        </div>
      </div>
    </div>
  );
}
